import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AiSettingsService } from '../../../service/ai-settings.service';
import {
  AI_FEATURES,
  AiFeature,
  AiOrganizationSettings,
  AiSettingsLoadStatus,
  AiSettingsView,
  AiUserSettings,
  toggleFeature,
} from '../../../ai/ai-settings/ai-settings';

/** Which PUT a switch belongs to. Each PUT replaces that whole block, so a save always sends it in full. */
export type AiSettingsSection = 'org' | 'me';

/**
 * One key per switch: org:master, org:allow, org:f:<Feature>, me:master, me:f:<Feature>.
 * Saving state, errors and the revert on failure are tracked per key.
 */
export type AiToggleKey = string;

export type AiToggleState = 'saving' | 'saved' | 'error';

/** How long the "Saved" confirmation stays next to a switch. */
export const AI_SAVED_MS = 2500;

interface AiSnapshot {
  org: AiOrganizationSettings | null;
  me: AiUserSettings | null;
}

@Component({
  selector: 'app-ai-feature-setting',
  standalone: true,
  imports: [CommonModule, MatSlideToggleModule, MatIconModule],
  templateUrl: './ai-feature-setting.component.html',
  styleUrls: ['./ai-feature-setting.component.scss'],
})
export class AiFeatureSettingComponent implements OnInit, OnDestroy {
  readonly features = AI_FEATURES;

  status: AiSettingsLoadStatus = 'loading';
  view: AiSettingsView | null = null;
  /** What the switches show: the last server state plus any change not yet confirmed. */
  org: AiOrganizationSettings | null = null;
  me: AiUserSettings | null = null;
  confirmOff = false;

  /** Per switch: saving, saved (briefly) or error. Absent means idle. */
  toggleState: Record<AiToggleKey, AiToggleState> = {};
  /** Text for the polite live region: one message when the queue drains (saved and failed). */
  announcement = '';

  /**
   * Saves are serialized: at most one PUT is in flight, for either section. Every response
   * carries both blocks and the effective feature list, so two concurrent PUTs could land out
   * of order and publish a stale view to the rest of the app. Changes made while a PUT is in
   * flight collect in `dirty` and go out together in the next PUT, built from the switches'
   * latest values, so the final request always carries the final state.
   */
  private inFlight: AiSettingsSection | null = null;
  private readonly dirty: Record<AiSettingsSection, Set<AiToggleKey>> = { org: new Set(), me: new Set() };
  /** Last state the server confirmed. A failed switch reverts to this. */
  private confirmed: AiSnapshot = { org: null, me: null };
  /** The value a failed switch was trying to reach, so Retry can send it again. */
  private readonly failedIntent: Record<AiToggleKey, boolean> = {};
  private readonly savedTimers: Record<AiToggleKey, ReturnType<typeof setTimeout>> = {};
  /** A save failed: re-read the server once the queue drains. */
  private needsReload = false;
  private reloading = false;
  private destroyed = false;
  private readonly savedThisRun = new Set<AiToggleKey>();
  private readonly failedThisRun = new Set<AiToggleKey>();
  private announceTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly subs = new Subscription();

  constructor(private ai: AiSettingsService, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.subs.add(this.ai.loadStatus$().subscribe((s) => (this.status = s)));
    this.subs.add(this.ai.view$().subscribe((v) => this.apply(v)));
  }

  ngOnDestroy(): void {
    // Saves already queued still go out (the request subscriptions are not in `subs`); a
    // failure after this point is reported by toast, since the row is gone.
    this.destroyed = true;
    this.subs.unsubscribe();
    if (this.announceTimer) clearTimeout(this.announceTimer);
    Object.keys(this.savedTimers).forEach((k) => clearTimeout(this.savedTimers[k]));
  }

  /** Loaded, but the API gave this user nothing to manage (404, or neither block present). */
  get notAllowed(): boolean {
    return this.status === 'notAllowed' || (this.status === 'ready' && !this.org && !this.me);
  }

  /** True while any switch change is still on its way to the server. */
  get busy(): boolean {
    return this.inFlight !== null || this.reloading || this.dirty.org.size > 0 || this.dirty.me.size > 0;
  }

  retry(): void {
    this.confirmOff = false;
    this.ai.refresh();
  }

  orgFeatureOn(f: AiFeature): boolean {
    return !!this.org && !this.org.disabledFeatures.includes(f);
  }

  myFeatureOn(f: AiFeature): boolean {
    return !!this.me && !this.me.disabledFeatures.includes(f);
  }

  orgAllows(f: AiFeature): boolean {
    return !this.org ? true : this.org.aiEnabled && !this.org.disabledFeatures.includes(f);
  }

  orgKey(f: AiFeature): AiToggleKey {
    return 'org:f:' + f;
  }

  myKey(f: AiFeature): AiToggleKey {
    return 'me:f:' + f;
  }

  /**
   * Turning AI on saves at once. Turning it off hides AI for everyone, so it asks first; the
   * switch snaps back to On until the admin confirms.
   */
  onMasterToggle(on: boolean, source?: MatSlideToggle): void {
    if (!this.org) return;
    if (!on) {
      if (source) {
        // The [checked] binding is still true, so Angular will not push it again: put both the
        // toggle and its native input back to On by hand, or the next click reads as "turn on".
        source.checked = true;
        if (source._inputElement) source._inputElement.nativeElement.checked = true;
      }
      this.confirmOff = true;
      return;
    }
    this.confirmOff = false;
    this.change('org:master', true);
  }

  confirmTurnOff(): void {
    this.confirmOff = false;
    this.change('org:master', false);
  }

  cancelTurnOff(): void {
    this.confirmOff = false;
  }

  setOrgFeature(f: AiFeature, on: boolean): void {
    this.change(this.orgKey(f), on);
  }

  setAllowOptOut(on: boolean): void {
    this.change('org:allow', on);
  }

  setMyAi(on: boolean): void {
    this.change('me:master', on);
  }

  setMyFeature(f: AiFeature, on: boolean): void {
    this.change(this.myKey(f), on);
  }

  /** Send the value a failed switch was trying to reach again. */
  retryToggle(key: AiToggleKey): void {
    if (!(key in this.failedIntent)) return;
    this.change(key, this.failedIntent[key]);
  }

  /** Accessible name of a switch, also used in its messages. Org and personal names differ. */
  labelFor(key: AiToggleKey): string {
    if (key === 'org:master') return 'AI features for the organization';
    if (key === 'org:allow') return 'Allow users to manage their own AI setting';
    if (key === 'me:master') return 'My AI features';
    const f = key.split(':')[2];
    const label = AI_FEATURES.find((x) => x.key === f)?.label ?? f;
    return label + (key.startsWith('org:') ? ' for the organization' : ' for my account');
  }

  /** id of a row's description, for the switch's aria-describedby. */
  descId(key: AiToggleKey): string {
    return 'ai-desc-' + key.replace(/:/g, '-');
  }

  /** Closing or reloading the browser tab mid-save would drop the change: ask first. */
  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: BeforeUnloadEvent): string | undefined {
    if (!this.busy) return undefined;
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  private change(key: AiToggleKey, on: boolean): void {
    const section = sectionOf(key);
    const snap = writeValue({ org: this.org, me: this.me }, key, on);
    if (!snap[section]) return;
    this.org = snap.org;
    this.me = snap.me;
    delete this.failedIntent[key];
    this.clearSavedTimer(key);
    this.toggleState = { ...this.toggleState, [key]: 'saving' };
    this.dirty[section].add(key);
    this.flush();
  }

  private flush(): void {
    if (this.inFlight || this.reloading) return;
    const section: AiSettingsSection | null = this.dirty.org.size ? 'org' : this.dirty.me.size ? 'me' : null;
    if (!section) {
      this.drained();
      return;
    }
    const keys = Array.from(this.dirty[section]);
    this.dirty[section].clear();
    this.inFlight = section;
    const request$ =
      section === 'org'
        ? this.ai.updateOrganization(cloneOrg(this.org as AiOrganizationSettings))
        : this.ai.updateMe(cloneMe(this.me as AiUserSettings));
    let settled = false;
    request$.subscribe({
      next: (v) => {
        if (settled) return;
        settled = true;
        // A 200 without a usable view: the server may not have applied it. Not "Saved".
        if (v) this.saved(section, keys, v);
        else this.failed(section, keys, { status: 404 });
      },
      error: (e: HttpErrorResponse) => {
        if (settled) return;
        settled = true;
        this.failed(section, keys, e);
      },
      // Completed without an answer (an interceptor swallowed it): never leave the queue wedged.
      complete: () => {
        if (settled) return;
        settled = true;
        this.failed(section, keys, null);
      },
    });
  }

  /** Queue empty. Reconcile with the server if anything failed, then announce once. */
  private drained(): void {
    if (this.needsReload) {
      // A failed PUT may still have committed (lost response, gateway timeout after the write),
      // so the reverted switches could be wrong. Re-read the server so this page and the app's
      // AI state show the truth. New saves wait for it, so its answer cannot land after theirs.
      this.needsReload = false;
      this.reloading = true;
      this.confirmOff = false;
      this.ai.reload().subscribe({
        complete: () => {
          this.reloading = false;
          this.flush();
        },
      });
      return;
    }
    // Show exactly what the server holds now (the last response or the reload).
    this.apply(this.view);
    const saved = Array.from(this.savedThisRun);
    const failed = Array.from(this.failedThisRun);
    this.savedThisRun.clear();
    this.failedThisRun.clear();
    const parts: string[] = [];
    if (saved.length) parts.push(saved.length === 1 ? 'Saved ' + this.labelFor(saved[0]) + '.' : 'Saved.');
    if (failed.length) {
      parts.push(
        failed.length === 1 ? 'Could not save ' + this.labelFor(failed[0]) + '.' : 'Could not save ' + failed.length + ' changes.'
      );
    }
    if (parts.length) this.announce(parts.join(' '));
  }

  /** Clear first, then set, so the same message twice in a row is still spoken. */
  private announce(message: string): void {
    this.announcement = '';
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = undefined;
      this.announcement = message;
    }, 0);
  }

  private saved(section: AiSettingsSection, keys: AiToggleKey[], v: AiSettingsView): void {
    this.inFlight = null;
    this.view = v;
    if (section === 'org') this.confirmed.org = v.organization ? cloneOrg(v.organization) : null;
    else this.confirmed.me = v.user ? cloneMe(v.user) : null;
    const done = keys.filter((k) => !this.dirty[section].has(k));
    const next = { ...this.toggleState };
    for (const k of done) {
      next[k] = 'saved';
      this.savedThisRun.add(k);
      this.failedThisRun.delete(k);
      this.savedTimers[k] = setTimeout(() => this.clearSaved(k), AI_SAVED_MS);
    }
    this.toggleState = next;
    this.flush();
  }

  private failed(section: AiSettingsSection, keys: AiToggleKey[], e: { status?: number } | null): void {
    this.inFlight = null;
    // A 404 means this user may no longer save this section (an admin changed the organization
    // meanwhile): drop this section's queued changes too, as failures with Retry. The other
    // section's queue still goes out.
    const notAllowed = e?.status === 404;
    const dropped = notAllowed ? [...keys, ...Array.from(this.dirty[section])] : keys;
    if (notAllowed) this.dirty[section].clear();
    const next = { ...this.toggleState };
    const reverted: AiToggleKey[] = [];
    let snap: AiSnapshot = { org: this.org, me: this.me };
    for (const k of Array.from(new Set(dropped))) {
      // Changed again while this PUT was in flight: that newer value is queued, keep it.
      if (this.dirty[section].has(k)) continue;
      this.failedIntent[k] = readValue(snap, k);
      snap = writeValue(snap, k, readValue(this.confirmed, k));
      next[k] = 'error';
      reverted.push(k);
      this.failedThisRun.add(k);
      this.savedThisRun.delete(k);
    }
    this.org = snap.org;
    this.me = snap.me;
    this.toggleState = next;
    this.needsReload = true;
    if (this.destroyed && reverted.length) {
      // The user already left the tab and cannot see the row error.
      const names = reverted.map((k) => this.labelFor(k)).join(', ');
      this.toastr.error('Could not save ' + names + '. Open Settings > AI Features to retry.');
    }
    this.flush();
  }

  private clearSaved(key: AiToggleKey): void {
    delete this.savedTimers[key];
    if (this.toggleState[key] !== 'saved') return;
    const next = { ...this.toggleState };
    delete next[key];
    this.toggleState = next;
  }

  private clearSavedTimer(key: AiToggleKey): void {
    if (this.savedTimers[key]) clearTimeout(this.savedTimers[key]);
    delete this.savedTimers[key];
  }

  private apply(v: AiSettingsView | null): void {
    this.view = v;
    // A save is in progress: the switches show the user's intended state, and each response
    // updates `confirmed` for its own section. Overwriting here would flip queued changes back.
    if (this.busy) return;
    this.org = v?.organization ? cloneOrg(v.organization) : null;
    this.me = v?.user ? cloneMe(v.user) : null;
    if (!this.org) this.confirmOff = false;
    this.confirmed = {
      org: this.org ? cloneOrg(this.org) : null,
      me: this.me ? cloneMe(this.me) : null,
    };
  }
}

function sectionOf(key: AiToggleKey): AiSettingsSection {
  return key.startsWith('org:') ? 'org' : 'me';
}

function cloneOrg(o: AiOrganizationSettings): AiOrganizationSettings {
  return { aiEnabled: !!o.aiEnabled, disabledFeatures: [...(o.disabledFeatures || [])], allowUserAiOptOut: !!o.allowUserAiOptOut };
}

function cloneMe(m: AiUserSettings): AiUserSettings {
  return { aiEnabled: !!m.aiEnabled, disabledFeatures: [...(m.disabledFeatures || [])] };
}

function readValue(s: AiSnapshot, key: AiToggleKey): boolean {
  const [section, kind, feature] = key.split(':');
  const block = section === 'org' ? s.org : s.me;
  if (!block) return false;
  if (kind === 'master') return block.aiEnabled;
  if (kind === 'allow') return !!(block as AiOrganizationSettings).allowUserAiOptOut;
  return !block.disabledFeatures.includes(feature as AiFeature);
}

function writeValue(s: AiSnapshot, key: AiToggleKey, on: boolean): AiSnapshot {
  const [section, kind, feature] = key.split(':');
  if (section === 'org') {
    if (!s.org) return s;
    const org = cloneOrg(s.org);
    if (kind === 'master') org.aiEnabled = on;
    else if (kind === 'allow') org.allowUserAiOptOut = on;
    else org.disabledFeatures = toggleFeature(org.disabledFeatures, feature as AiFeature, on);
    return { ...s, org };
  }
  if (!s.me) return s;
  const me = cloneMe(s.me);
  if (kind === 'master') me.aiEnabled = on;
  else me.disabledFeatures = toggleFeature(me.disabledFeatures, feature as AiFeature, on);
  return { ...s, me };
}
